import { useTheme } from '@supply-mover-2-0/web-ui';
import { useEffect } from 'react';
import { usePortalSiteStore } from '../stores/portalSiteStore';

/**
 * Applies the active portal_site's brand theme to the document.
 *
 * Two-axis resolution:
 *   - per-tenant fields from the bootstrap (`primaryColor`, `accentColor`,
 *     `loginBackgroundColor`, `faviconUrl`, plus their `*Light` light-mode
 *     overrides)
 *   - active theme from the storefront's ThemeProvider (`isDark`)
 *
 * Effective color = light-mode override when the user is in light mode AND
 * the tenant has configured a `*Light` value, otherwise the dark/default
 * value. This keeps single-color tenants working unchanged while letting
 * tenants who care about light mode swap their palette per theme.
 *
 * The login surfaces (LoginModal / HomePage welcome / RegisterPage) are
 * intentionally left out of the light-mode swap — pre-login screens always
 * render against the dark/default primary so the brand identity stays put
 * before sign-in. Theme only takes effect post-login.
 *
 *   - `primaryColor` / `accentColor` (effective) → CSS variables on :root,
 *     so any stylesheet (Tailwind utilities or hand-written CSS) can
 *     resolve `var(--brand-primary)` / `var(--brand-accent)` at runtime.
 *   - `faviconUrl` → swap the <link rel="icon"> + Apple touch icon. Not
 *     theme-aware (browsers handle their own dark-mode favicon swap).
 *   - `primaryColor` (effective) → also drives the mobile browser chrome
 *     via <meta name="theme-color">.
 *
 * Renders nothing. Intentionally a side-effect component so it can sit
 * inside <TenantGate> and react when the bootstrap response lands or the
 * theme toggles.
 */
const FAVICON_REL_VALUES = ['icon', 'shortcut icon'];
const APPLE_TOUCH_REL = 'apple-touch-icon';
const META_THEME_COLOR = 'theme-color';

function setOrCreateMeta(name: string, value: string): void {
	let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
	if (el == null) {
		el = document.createElement('meta');
		el.setAttribute('name', name);
		document.head.appendChild(el);
	}
	el.setAttribute('content', value);
}

/**
 * Parse a 3- or 6-digit hex color into an [r, g, b] triplet (0–255 each).
 * Returns null for unparseable input — caller decides the fallback.
 */
function parseHexColor(hex: string): [number, number, number] | null {
	const trimmed = hex.trim().replace(/^#/, '');
	const expanded =
		trimmed.length === 3
			? trimmed
					.split('')
					.map((c) => c + c)
					.join('')
			: trimmed;
	if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
		return null;
	}
	return [
		parseInt(expanded.slice(0, 2), 16),
		parseInt(expanded.slice(2, 4), 16),
		parseInt(expanded.slice(4, 6), 16),
	];
}

/**
 * Pick a readable foreground color for a given background hex.
 *
 * Uses the W3C relative-luminance formula and the WCAG "use the higher
 * contrast" guideline: bright surfaces get near-black text, dark surfaces
 * get white. The 0.5 threshold lands roughly where the eye perceives
 * mid-gray — empirically tested against the brand colors we expect
 * (HF red, navy, off-whites). Returned as a space-separated `R G B`
 * string so it can be dropped into a Tailwind `<alpha-value>`-aware
 * color definition (see tailwind.config.js `brand.primary-fg`).
 *
 * Defaults to "255 255 255" (white) on unparseable input — that matches
 * the historical hardcoded `text-white` header style.
 */
function readableForegroundRgb(hex: string | null): string {
	if (hex == null) {
		return '255 255 255';
	}
	const rgb = parseHexColor(hex);
	if (rgb == null) {
		return '255 255 255';
	}
	const [r, g, b] = rgb;
	// Standard sRGB → relative-luminance approximation. We don't bother
	// with the gamma-correction step — the threshold is already loose
	// enough that the linearized version flips on the same colors.
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
	// "Near-black" rather than pure black so the text reads as ink on the
	// brand color rather than a hard system black.
	return luminance > 0.5 ? '15 23 42' : '255 255 255';
}

function setOrCreateLink(rel: string, href: string): void {
	let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
	if (el == null) {
		el = document.createElement('link');
		el.setAttribute('rel', rel);
		document.head.appendChild(el);
	}
	el.setAttribute('href', href);
}

export function BrandThemeApplier(): null {
	// Tenant-configured values from the bootstrap.
	const primaryColor = usePortalSiteStore((s) => s.site?.primaryColor ?? null);
	const primaryColorLight = usePortalSiteStore(
		(s) => s.site?.primaryColorLight ?? null,
	);
	const accentColor = usePortalSiteStore((s) => s.site?.accentColor ?? null);
	const accentColorLight = usePortalSiteStore(
		(s) => s.site?.accentColorLight ?? null,
	);
	const pageBackgroundColor = usePortalSiteStore(
		(s) => s.site?.pageBackgroundColor ?? null,
	);
	const pageBackgroundColorLight = usePortalSiteStore(
		(s) => s.site?.pageBackgroundColorLight ?? null,
	);
	const surfaceBackgroundColor = usePortalSiteStore(
		(s) => s.site?.surfaceBackgroundColor ?? null,
	);
	const surfaceBackgroundColorLight = usePortalSiteStore(
		(s) => s.site?.surfaceBackgroundColorLight ?? null,
	);
	const loginBackgroundColor = usePortalSiteStore(
		(s) => s.site?.loginBackgroundColor ?? null,
	);
	const faviconUrl = usePortalSiteStore((s) => s.site?.faviconUrl ?? null);

	// Active theme. `isDark` flips when the user toggles in UserMenu, and
	// the effects below re-run to swap the CSS vars in place.
	const { isDark } = useTheme();

	// Effective values: prefer the light-mode override only when in light
	// mode AND the override is non-empty. Falling back to the dark/default
	// value means tenants who haven't configured the light variant just
	// keep their existing palette in both modes — no surprise UI changes.
	const effectivePrimary =
		!isDark && primaryColorLight != null && primaryColorLight.length > 0
			? primaryColorLight
			: primaryColor;
	const effectiveAccent =
		!isDark && accentColorLight != null && accentColorLight.length > 0
			? accentColorLight
			: accentColor;

	useEffect(() => {
		const root = document.documentElement;
		if (effectivePrimary != null && effectivePrimary.length > 0) {
			root.style.setProperty('--brand-primary', effectivePrimary);
			// Readable foreground for any surface painted with
			// `bg-brand-primary` — flips between near-black and white
			// depending on the active primary's luminance, so a tenant who
			// configures a near-white primaryColorLight gets dark text on
			// the header in light mode while one who picks a darker red gets
			// white text in both modes. Consumed via the `text-brand-primary-fg`
			// Tailwind utility (and its alpha-modifier variants) defined in
			// tailwind.config.js.
			root.style.setProperty(
				'--brand-primary-fg-rgb',
				readableForegroundRgb(effectivePrimary),
			);
			setOrCreateMeta(META_THEME_COLOR, effectivePrimary);
		} else {
			root.style.removeProperty('--brand-primary');
			root.style.removeProperty('--brand-primary-fg-rgb');
		}
	}, [effectivePrimary]);

	// Mode-aware page background. Branded dark mode means the body bg
	// picks up the tenant's primary color rather than dropping to a
	// generic dark gray. Resolution order:
	//   1. Explicit field for the active mode (pageBackgroundColor /
	//      pageBackgroundColorLight) — admin-configured.
	//   2. Auto-derived from `effectivePrimary`:
	//        - light mode: 4% primary mixed over white (the historical
	//          warm-tint behavior — keeps unchanged tenants looking the
	//          same as before this dark-mode work).
	//        - dark mode: 22% primary mixed over near-black (#0a0f14) —
	//          a deep, slightly-tinted-towards-brand surface that reads
	//          as "darker tenant" instead of generic gray.
	//   3. Tailwind config fallback (#fafaf6 light / #0a0f14 dark) when
	//      no primary AND no explicit page bg is set.
	// `--brand-page-bg-soft` is a lighter-by-mode variant for any UI
	// that wants a sliver of contrast against the page (filter
	// chrome, subdued banners). Same resolution rules.
	useEffect(() => {
		const root = document.documentElement;
		// Light mode must NOT inherit the dark pageBackgroundColor. A tenant
		// that configured only a dark page bg (no *Light variant — e.g. Capital
		// Trading) would otherwise bleed its dark color into light mode and
		// produce dark-on-dark text. When the light variant is absent we leave
		// `explicit` null and fall through to the mode-appropriate auto-derive
		// below (a clean light tint), exactly as a tenant with no explicit page
		// bg at all gets.
		const explicit = isDark ? pageBackgroundColor : pageBackgroundColorLight;
		if (explicit != null && explicit.length > 0) {
			root.style.setProperty('--brand-page-bg', explicit);
			// Soft variant: 6% primary mixed onto the explicit page bg,
			// or a small black/white tweak when no primary is available.
			if (effectivePrimary != null && effectivePrimary.length > 0) {
				root.style.setProperty(
					'--brand-page-bg-soft',
					`color-mix(in srgb, ${effectivePrimary} 6%, ${explicit})`,
				);
			} else {
				root.style.setProperty(
					'--brand-page-bg-soft',
					`color-mix(in srgb, ${explicit} 92%, ${isDark ? 'white' : 'black'})`,
				);
			}
			return;
		}

		if (effectivePrimary != null && effectivePrimary.length > 0) {
			if (isDark) {
				// Dark-mode page bg = the brand primary itself (with a hair
				// of black mixed in so the page sits very slightly darker
				// than the header / BottomNav, which paint pure primary).
				// Earlier iterations used 22% primary over near-black —
				// that read as "dark gray with a tint" instead of the
				// recognizable brand color users see in the footer. The
				// goal here is "this is HF Foods, in the dark" not "this
				// is a generic dark-gray app with HF accents".
				root.style.setProperty(
					'--brand-page-bg',
					`color-mix(in srgb, ${effectivePrimary} 92%, black)`,
				);
				// Soft variant sits slightly above page bg (lighter) for
				// any UI that wants a sliver of contrast — filter
				// chrome, subdued banners, dividers.
				root.style.setProperty(
					'--brand-page-bg-soft',
					`color-mix(in srgb, ${effectivePrimary} 96%, white)`,
				);
			} else {
				root.style.setProperty(
					'--brand-page-bg',
					`color-mix(in srgb, ${effectivePrimary} 4%, white)`,
				);
				root.style.setProperty(
					'--brand-page-bg-soft',
					`color-mix(in srgb, ${effectivePrimary} 8%, white)`,
				);
			}
			return;
		}

		// No primary color AND no explicit page bg: pick a sensible default
		// per mode. We can't lean on Tailwind's hardcoded fallback alone
		// because the same `bg-brand-page-bg` class needs to render dark
		// for dark mode, light for light — explicitly writing the var
		// avoids the white-page-in-dark-mode failure the var-fallback would
		// otherwise produce.
		if (isDark) {
			root.style.setProperty('--brand-page-bg', '#0a0f14');
			root.style.setProperty('--brand-page-bg-soft', '#11161c');
		} else {
			root.style.setProperty('--brand-page-bg', '#fafaf6');
			root.style.setProperty('--brand-page-bg-soft', '#f5f3ec');
		}
	}, [effectivePrimary, isDark, pageBackgroundColor, pageBackgroundColorLight]);

	// Mode-aware surface (card / modal / drawer) background. Sits one
	// step "above" the page bg so cards always read as elevated on
	// either the light or dark surface. Resolution order mirrors page-bg:
	//   1. Explicit surfaceBackgroundColor / surfaceBackgroundColorLight.
	//   2. Auto-derived: white in light mode (the long-standing card
	//      style); 32% primary mixed over near-black in dark mode (a
	//      noticeably-lighter-than-page brand-tinted surface).
	//   3. Tailwind config fallback when nothing is configured.
	// Storefront card / modal / drawer styles read this via the
	// `bg-brand-surface` Tailwind utility — see tailwind.config.js.
	useEffect(() => {
		const root = document.documentElement;
		// Same rule as the page bg above: light mode must not inherit the dark
		// surfaceBackgroundColor; a missing light variant falls through to the
		// plain-white light-mode card below.
		const explicit = isDark
			? surfaceBackgroundColor
			: surfaceBackgroundColorLight;
		if (explicit != null && explicit.length > 0) {
			root.style.setProperty('--brand-surface', explicit);
			return;
		}

		if (isDark) {
			if (effectivePrimary != null && effectivePrimary.length > 0) {
				// Surface (cards, modals, drawer) = primary lightened by
				// ~14% white. Sits noticeably above the page bg (which is
				// primary darkened ~8%) so cards always read as elevated
				// against the background, but stays clearly within the
				// brand-color family — no jarring jump to gray.
				root.style.setProperty(
					'--brand-surface',
					`color-mix(in srgb, ${effectivePrimary} 86%, white)`,
				);
			} else {
				// Dark mode, no primary: a generic-but-not-white dark gray
				// keeps cards readable instead of falling through to the
				// Tailwind fallback (#ffffff) and producing white cards on
				// a dark page.
				root.style.setProperty('--brand-surface', '#1a1d22');
			}
			return;
		}

		// Default light-mode surface stays plain white — matches the
		// historical card / modal styling that a thousand `bg-white`
		// references depend on.
		root.style.setProperty('--brand-surface', '#ffffff');
	}, [
		effectivePrimary,
		isDark,
		surfaceBackgroundColor,
		surfaceBackgroundColorLight,
	]);

	// Secondary / "muted" text — used for placeholders, helper text,
	// "Sign in for pricing" lock-icon caption, manufacturer name under
	// product titles, secondary timestamps, etc. Generic `text-gray-500
	// dark:text-gray-400` against a brand-tinted dark page reads as
	// out-of-family low-contrast gray; a brand-tinted light text reads
	// as proper muted-but-on-brand.
	useEffect(() => {
		const root = document.documentElement;
		if (effectivePrimary != null && effectivePrimary.length > 0) {
			if (isDark) {
				// Muted text — placeholders, helper copy ("Sign in to see
				// your balance"), metric labels, manufacturer names,
				// timestamps. It must stay legible on the LIGHTEST surface
				// it can land on, which is `--brand-surface` (the elevated
				// card), NOT just the darker page bg. At a 25% primary mix
				// it cleared AA against the page bg (~6:1) but only hit
				// ~4:1 on the lighter card surface (PD-14165) — secondary
				// text inside cards read as washed-out. A heavier white
				// mix (88%) clears WCAG AA (~5:1) on the card surface while
				// still carrying a hint of brand tint and reading dimmer
				// than the gray-100 headings.
				root.style.setProperty(
					'--brand-text-muted',
					`color-mix(in srgb, ${effectivePrimary} 12%, white)`,
				);
				// Card / input / divider border. Sits between surface and
				// text-muted so the edge is visible but never assertive.
				root.style.setProperty(
					'--brand-border',
					`color-mix(in srgb, ${effectivePrimary} 55%, white)`,
				);
				// Input bg = page bg. Inputs sit inside cards (which are
				// lighter than the page), so making the input the same
				// shade as the page gives a recessed-into-the-card feel
				// without going so dark it reads as charcoal. Earlier
				// iterations used `primary 75% + black 25%` (looked like
				// charcoal-against-green which the user flagged); using
				// `primary 92% + black 8%` (matches `--brand-page-bg`)
				// keeps the input clearly in the brand-green family.
				root.style.setProperty(
					'--brand-input',
					`color-mix(in srgb, ${effectivePrimary} 92%, black)`,
				);
			} else {
				// Light mode: subtle brand tint over neutral grays so the
				// hue family matches the rest of the page without
				// sacrificing the gray-on-white legibility users expect.
				root.style.setProperty(
					'--brand-text-muted',
					`color-mix(in srgb, ${effectivePrimary} 12%, #6b7280)`,
				);
				root.style.setProperty(
					'--brand-border',
					`color-mix(in srgb, ${effectivePrimary} 8%, #e5e7eb)`,
				);
				root.style.setProperty(
					'--brand-input',
					`color-mix(in srgb, ${effectivePrimary} 4%, #f9fafb)`,
				);
			}
			return;
		}

		// No primary configured: fall through to neutral gray defaults so
		// non-branded tenants still get sensible muted text + borders +
		// input backgrounds.
		if (isDark) {
			root.style.setProperty('--brand-text-muted', '#9ca3af');
			root.style.setProperty('--brand-border', '#374151');
			root.style.setProperty('--brand-input', '#1f2937');
		} else {
			root.style.setProperty('--brand-text-muted', '#6b7280');
			root.style.setProperty('--brand-border', '#e5e7eb');
			root.style.setProperty('--brand-input', '#f9fafb');
		}
	}, [effectivePrimary, isDark]);

	useEffect(() => {
		const root = document.documentElement;
		if (effectiveAccent != null && effectiveAccent.length > 0) {
			root.style.setProperty('--brand-accent', effectiveAccent);
			// Also override the existing storefront `portal.accent` token so
			// every Tailwind utility that already reads it (e.g. `bg-portal-accent`
			// on the Sign In CTA) picks up the brand without code changes.
			// Hover state is just a 12%-darkened version computed via
			// color-mix so we don't need a second admin field.
			root.style.setProperty('--color-portal-accent', effectiveAccent);
			root.style.setProperty(
				'--color-portal-accent-hover',
				`color-mix(in srgb, ${effectiveAccent} 88%, black)`,
			);
			// `portal.cart` is the "active / in-cart" token (header cart-qty
			// pill, sidebar nav cart badge, BottomNav cart indicator). Default
			// tailwind config keeps it green-600 because the historical
			// e-commerce convention is a green "added to cart" affordance,
			// but tenants that pick a non-green accent (or a different green
			// shade) want the badge to track the brand. Override both the
			// solid and the hover tokens here so every consumer of
			// `bg-portal-cart` / `text-portal-cart` follows the active brand
			// without any per-utility code changes.
			root.style.setProperty('--color-portal-cart', effectiveAccent);
			root.style.setProperty(
				'--color-portal-cart-hover',
				`color-mix(in srgb, ${effectiveAccent} 88%, black)`,
			);
		} else {
			root.style.removeProperty('--brand-accent');
			root.style.removeProperty('--color-portal-accent');
			root.style.removeProperty('--color-portal-accent-hover');
			root.style.removeProperty('--color-portal-cart');
			root.style.removeProperty('--color-portal-cart-hover');
		}
	}, [effectiveAccent]);

	// Login backdrop bleed color — surfaced as `--brand-login-bg`.
	// Falls back to the DARK/default primaryColor (not the effective one)
	// so the pre-login surface stays anchored to the brand's identity color
	// regardless of the user's persisted theme preference. Tenants who
	// want a different login backdrop set `loginBackgroundColor` explicitly.
	useEffect(() => {
		const root = document.documentElement;
		const effective =
			loginBackgroundColor != null && loginBackgroundColor.length > 0
				? loginBackgroundColor
				: primaryColor;
		if (effective != null && effective.length > 0) {
			root.style.setProperty('--brand-login-bg', effective);
		} else {
			root.style.removeProperty('--brand-login-bg');
		}
	}, [loginBackgroundColor, primaryColor]);

	useEffect(() => {
		if (faviconUrl == null || faviconUrl.length === 0) {
			return;
		}
		// `shortcut icon` is the legacy attr — some Workd-default tooling
		// emits both, so we rewrite both rather than leave a stale one.
		for (const rel of FAVICON_REL_VALUES) {
			setOrCreateLink(rel, faviconUrl);
		}
		setOrCreateLink(APPLE_TOUCH_REL, faviconUrl);
	}, [faviconUrl]);

	return null;
}
